import { AppBar, Toolbar, Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Badge, Divider, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { logout } from "../../services/auth.service";
import { useEffect, useState } from "react";
import Logo from "../../assets/logo6.png";
import AccountCircle from "@mui/icons-material/AccountCircle";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../router/constants";
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import dayjs from "dayjs";
import {
  getUnreadNotificationsCount,
  listMyNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  NotificationItem,
} from "../../services/notifications.service";

export function Header() {
  const { user } = useSelector((state: RootState) => state.userInfo);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const notifOpen = Boolean(notifAnchorEl);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifs, setNotifs] = useState<NotificationItem[]>([]);

  const navigate = useNavigate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const loadUnread = async () => {
    try {
      const c = await getUnreadNotificationsCount();
      setUnreadCount(c);
    } catch {
      // ignore
    }
  };

  const loadNotifs = async () => {
    try {
      const res = await listMyNotifications({ take: 8 });
      setNotifs(res.items);
      setUnreadCount(res.unreadCount);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadUnread();
    const t = setInterval(() => loadUnread(), 30_000);
    return () => clearInterval(t);
  }, []);

  const handleNotifOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
    loadNotifs();
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleNotifClick = async (n: NotificationItem) => {
    try {
      if (!n.wasRead) {
        await markNotificationAsRead(n.id);
      }
    } catch {}

    handleNotifClose();
    loadUnread();

    if (n.url) {
      navigate(n.url);
    }
  };

  const handleNotifReadAll = async () => {
    await markAllNotificationsAsRead().catch(() => undefined);
    await loadNotifs();
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleGoToProfile = () => {
    handleMenuClose();
    if (user.role === "DOCTOR") {
      navigate(ROUTES.EDIT_DOCTOR_PROFILE);
    } else {
      navigate(ROUTES.EDIT_PROFILE);
    }
  };

  const handleChangePassword = () => {
    handleMenuClose();
    navigate(ROUTES.CHANGE_PASSWORD);
  };

  const handleOpenTerms = () => {
    handleMenuClose();
    // Exemplo: navigate(ROUTES.TERMS);
    window.open("/termos-de-uso", "_blank");
  };
 

  const handleOpenHelp = () => {
    handleMenuClose();
    window.open("/ajuda", "_blank");
  };

  const handleOpenMyAppointments = () => {
    handleMenuClose();
    window.open("/meus-agendamentos", "_blank");
  };

  const handleLogoClick = () => {
    const isUserRole = user?.role?.toLowerCase() === "user";
    navigate(isUserRole ? ROUTES.HOME : ROUTES.DASHBOARD);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        {/* Logo */}
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          onClick={handleLogoClick}
          sx={{ cursor: "pointer" }}
        >
          <img src={Logo} alt="Logo" style={{ height: 30 }} />
        </Box>

        {/* User Info */}
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            display={{ xs: "none", sm: "flex" }}
            flexDirection="column"
            alignItems="flex-end"
          >
           
          </Box>

          {/* Notificações */}
          <IconButton
            sx={{ color: "#133859" }}
            onClick={handleNotifOpen}
          >
            <Badge color="error" badgeContent={unreadCount} max={99}>
              <NotificationsNoneIcon />
            </Badge>
          </IconButton>

          <Menu
            anchorEl={notifAnchorEl}
            open={notifOpen}
            onClose={handleNotifClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography fontWeight={700}>Notificações</Typography>
                <Typography variant="caption" color="text.secondary">
                  {unreadCount} não lidas
                </Typography>
              </Stack>
            </Box>
            <Divider />

            {notifs.length === 0 && (
              <MenuItem disabled>
                <ListItemText primary="Sem notificações" />
              </MenuItem>
            )}

            {notifs.map((n) => (
              <MenuItem key={n.id} onClick={() => handleNotifClick(n)}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={n.wasRead ? 500 : 800}>
                        {n.title}
                      </Typography>
                      {!n.wasRead && (
                        <Badge variant="dot" color="error" />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(n.createdAt).format("DD/MM HH:mm")} — {n.body}
                    </Typography>
                  }
                />
              </MenuItem>
            ))}

            <Divider />

            <MenuItem onClick={handleNotifReadAll}>
              <ListItemText primary="Marcar tudo como lido" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleNotifClose();
                navigate(ROUTES.NOTIFICATIONS);
              }}
            >
              <ListItemText primary="Ver todas" />
            </MenuItem>
          </Menu>

          <Typography variant="body2" fontWeight={600} color="#133859">
            {user.name}
          </Typography>

          {/* Ícone de usuário */}
          <IconButton onClick={handleMenuOpen} sx={{ padding: 0, color: "#133859" }}>
            <AccountCircle sx={{ width: 40, height: 40 }} />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={handleGoToProfile}>
              <ListItemIcon>
                <EditIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Editar perfil"   
              primaryTypographyProps={{ color: "primary" }} />
            </MenuItem>

            <MenuItem onClick={handleOpenTerms}>
              <ListItemIcon>
                <DescriptionIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Termos de uso" primaryTypographyProps={{ color: "primary" }} />
            </MenuItem>

            <MenuItem onClick={handleOpenHelp}>
              <ListItemIcon>
                <HelpOutlineIcon fontSize="small"  color="primary" />
              </ListItemIcon>
              <ListItemText primary="Ajuda"  primaryTypographyProps={{ color: "primary" }}/>
            </MenuItem>

            <MenuItem onClick={handleOpenMyAppointments}>
              <ListItemIcon>
                <HistoryOutlinedIcon fontSize="small"  color="primary" />
              </ListItemIcon>
              <ListItemText primary="Meus agendamentos" primaryTypographyProps={{ color: "primary" }}/>
            </MenuItem>

            <MenuItem onClick={handleChangePassword}>
              <ListItemIcon>
                <LockOutlinedIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Trocar senha" primaryTypographyProps={{ color: "primary" }} />
            </MenuItem>

            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small"color="error" />
              </ListItemIcon>
              <ListItemText primary="Sair"primaryTypographyProps={{ color: "error" }}/>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
